import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoader } from "@/components/common/Loading";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Square } from "lucide-react";
import { Field, StateSelect } from "@/pages/portal/PortalLicenses";

export default function PortalBackgroundCheck() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/portal/background-check").then((r) => {
    setData(r.data);
    const req = r.data.request;
    setForm({
      legal_first_name: req?.legal_first_name || r.data.prefill?.legal_first_name || "",
      legal_middle_name: req?.legal_middle_name || "",
      legal_last_name: req?.legal_last_name || r.data.prefill?.legal_last_name || "",
      legal_suffix: req?.legal_suffix || "",
      date_of_birth: req?.date_of_birth || "",
      address: req?.address || r.data.prefill?.address || "",
      city: req?.city || r.data.prefill?.city || "",
      state: req?.state || r.data.prefill?.state || "",
      postal_code: req?.postal_code || r.data.prefill?.postal_code || "",
      retail_readiness: req?.retail_readiness || { capital_available: "", funding_source: "", notes: "" },
      authorize: false,
    });
  }).catch(() => setData({}));

  useEffect(() => { load(); }, []);
  if (!data || !form) return <PageLoader label="Loading" />;

  const req = data.request;
  const locked = req && !req.editable;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setReady = (k, v) => setForm((f) => ({ ...f, retail_readiness: { ...f.retail_readiness, [k]: v } }));

  const submit = async () => {
    if (!form.legal_first_name.trim() || !form.legal_last_name.trim()) return toast.error("Please enter your legal first and last name.");
    if (!form.address.trim() || !form.city.trim() || !form.state || !form.postal_code.trim()) return toast.error("Please complete your current residential address.");
    if (!form.authorize) return toast.error("Please authorize Top-Recruit to initiate the screening.");
    setBusy(true);
    try {
      await api.post("/portal/background-check", {
        legal_first_name: form.legal_first_name.trim(),
        legal_middle_name: form.legal_middle_name.trim() || null,
        legal_last_name: form.legal_last_name.trim(),
        legal_suffix: form.legal_suffix.trim() || null,
        date_of_birth: form.date_of_birth.trim() || null,
        address: form.address.trim(), city: form.city.trim(), state: form.state, postal_code: form.postal_code.trim(),
        retail_readiness: data.is_retail ? form.retail_readiness : null,
        authorize: true,
      });
      toast.success("Request submitted. Your recruiter will begin the screening process.");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="portal-bgc">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-[#006090]/10 text-[#006090] flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5" /></span>
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Background &amp; Credit Check Authorization</h1>
          <p className="text-sm text-slate-600 mt-1">This information lets your recruiting team begin the official background &amp; credit screening (processed by our third-party partner, Business Information Group). We do <strong>not</strong> collect your Social Security number or driver's-license number here — those are handled directly and securely by the screening provider.</p>
        </div>
      </div>

      <p className="mt-6 flex items-center gap-2 text-sm font-semibold" data-testid="bgc-auth-status">
        {req ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Square className="w-5 h-5 text-slate-400" />}
        <span className={req ? "text-emerald-700" : "text-slate-600"}>Background Authorization Submitted</span>
      </p>
      {req && (
        <div className="mt-3 rounded-xl border border-[#006090]/20 bg-[#006090]/5 p-4 flex items-center justify-between" data-testid="portal-bgc-status">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase">Request {req.human_id}</p>
            <p className="mt-0.5 font-semibold text-slate-900">Status: {req.status_label}</p>
          </div>
          {locked && <span className="text-xs text-slate-500">Being processed — no longer editable</span>}
        </div>
      )}

      <div className={`mt-6 space-y-8 ${locked ? "opacity-70 pointer-events-none" : ""}`}>
        {/* Legal name */}
        <Section title="Legal Name" desc="Enter your full legal name exactly as it appears on your government ID.">
          <div className="grid sm:grid-cols-4 gap-4">
            <Field label="Legal First Name *"><Input data-testid="bgc-first" value={form.legal_first_name} onChange={(e) => set("legal_first_name", e.target.value)} /></Field>
            <Field label="Middle Name"><Input data-testid="bgc-middle" value={form.legal_middle_name} onChange={(e) => set("legal_middle_name", e.target.value)} /></Field>
            <Field label="Legal Last Name *"><Input data-testid="bgc-last" value={form.legal_last_name} onChange={(e) => set("legal_last_name", e.target.value)} /></Field>
            <Field label="Suffix"><Input data-testid="bgc-suffix" placeholder="Jr, Sr, III" value={form.legal_suffix} onChange={(e) => set("legal_suffix", e.target.value)} /></Field>
          </div>
          <Field label="Date of Birth (optional — month & year only)" className="mt-4 max-w-xs">
            <Input data-testid="bgc-dob" placeholder="MM/YYYY" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
          </Field>
        </Section>

        {/* Address */}
        <Section title="Current Residential Address">
          <Field label="Street Address *"><Input data-testid="bgc-address" value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <Field label="City *"><Input data-testid="bgc-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="State *">
              <StateSelect value={form.state} onChange={(v) => set("state", v)} testId="bgc-state" />
            </Field>
            <Field label="ZIP *"><Input data-testid="bgc-zip" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></Field>
          </div>
        </Section>

        {/* Retail financial readiness */}
        {data.is_retail && (
          <Section title="Retail Financial Readiness" desc="Retail agency candidates: a high-level snapshot of your financial readiness. No account or sensitive numbers required.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Approximate Capital Available">
                <Select value={form.retail_readiness.capital_available || ""} onValueChange={(v) => setReady("capital_available", v)}>
                  <SelectTrigger data-testid="bgc-capital"><SelectValue placeholder="Select a range" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["Under $25,000", "$25,000 – $50,000", "$50,000 – $100,000", "$100,000 – $250,000", "Over $250,000", "Prefer not to say"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Primary Funding Source">
                <Select value={form.retail_readiness.funding_source || ""} onValueChange={(v) => setReady("funding_source", v)}>
                  <SelectTrigger data-testid="bgc-funding"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["Personal Savings", "Financing / Loan", "Investor / Partner", "Combination", "Not sure yet"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Anything else you'd like your recruiter to know?" className="mt-4">
              <Textarea data-testid="bgc-readiness-notes" value={form.retail_readiness.notes || ""} onChange={(e) => setReady("notes", e.target.value)} rows={3} />
            </Field>
          </Section>
        )}

        {/* Authorization */}
        {!locked && (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <Checkbox checked={form.authorize} onCheckedChange={(v) => set("authorize", !!v)} data-testid="bgc-authorize" className="mt-0.5" />
              <span>I authorize Top-Recruit and its designated third-party screening provider (Business Information Group) to initiate a background and credit check as part of my recruiting process. I understand the provider will collect any additional information required directly and securely.</span>
            </label>
          </div>
        )}
      </div>

      {!locked && (
        <div className="mt-6">
          <Button onClick={submit} disabled={busy} data-testid="bgc-submit" className="bg-[#006090] hover:bg-[#004E75]">
            {busy ? "Submitting…" : req ? "Update Request" : "Submit Request"}
          </Button>
        </div>
      )}
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <section>
      <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
      {desc && <p className="text-sm text-slate-500 mt-0.5">{desc}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}
